// pages/TravelExpense/TravelBackOfficeQueue.js
//
// Go-live parity (2026-09-24): the legacy TEA back-office queues
// (TravelExpenseMain.vb / TE\ExpenseApproval.cs):
//   • HR          (module travel.hr)          "Awaiting Prepurchase" —
//     pre-arrangement requests not yet fulfilled (GET TravelExpense/hr/awaiting-prepurchase).
//   • Accounting  (module travel.accounting)  "Awaiting Reimbursement" —
//     travelers whose expenses are submitted and whose reimbursement is not
//     closed (no status filter), and "Overdue Expense Reports" — approved
//     trips 14+ days after return whose traveler has not submitted
//     (GET TravelExpense/accounting/awaiting-reimbursement | overdue).
// Each row opens the trip page on the right tab; the actions (mark
// fulfilled / close reimbursement) live there.

import React, { useEffect, useMemo, useState } from "react";
import { Container, Card, CardBody, Alert } from "reactstrap";
import { useNavigate } from "react-router-dom";
import { DashboardPageHeader, EmptyState, ResponsiveTabs, TableSkeleton } from "../../Components/Common/DashboardKit";
import exportRowsToCsv from "../../Components/Common/DashboardKit/exportCsv";
import api from "../../api";
import { toCamel } from "./travelCase";

const fmtDate = (d) => { if (!d) return "—"; try { return new Date(d).toLocaleDateString(); } catch { return "—"; } };
const needs = (r) => [r.airRequired && "Air", r.hotelRequired && "Hotel", r.rentalRequired && "Car"].filter(Boolean).join(", ") || "—";

const VIEWS = {
    hr: {
        title: "Travel — Awaiting Prepurchase",
        subtitle: "Pre-arrangement requests HR has not marked fulfilled yet",
        tabs: [{ key: "prepurchase", label: "Awaiting Prepurchase", url: "TravelExpense/hr/awaiting-prepurchase" }],
        columns: [
            ["Trip ID", (r) => r.tripId],
            ["Trip", (r) => r.nameOfTrip || ""],
            ["Trip Status", (r) => r.statusLabel || r.status],
            ["Requested By", (r) => r.submitByName || ""],
            ["Request Made On", (r) => fmtDate(r.requestedOn)],
            ["Starting Travel Date", (r) => fmtDate(r.leaveHomeDate)],
            ["Needs", needs],
        ],
        link: (r) => `/travel-expense/${r.tripId}?tab=arrangements`,
    },
    accounting: {
        title: "Travel — Accounting",
        subtitle: "Submitted trip expenses to review and close",
        tabs: [
            { key: "reimburse", label: "Awaiting Reimbursement", url: "TravelExpense/accounting/awaiting-reimbursement" },
            { key: "overdue", label: "Overdue Expense Reports", url: "TravelExpense/accounting/overdue" },
        ],
        columns: [
            ["Trip ID", (r) => r.tripId],
            ["Employee", (r) => r.employee || r.empid],
            ["Travel Description", (r) => r.nameOfTrip || ""],
            ["Date Prepared", (r) => fmtDate(r.submitDate)],
            ["Starting Travel Date", (r) => fmtDate(r.leaveHomeDate)],
            ["Return Date", (r) => fmtDate(r.returnHomeDate)],
            ["Status", (r) => r.statusLabel || r.status],
        ],
        link: (r) => `/travel-expense/${r.tripId}?tab=expenses&emp=${r.empid}`,
    },
};

const TravelBackOfficeQueue = ({ kind }) => {
    const view = VIEWS[kind] || VIEWS.hr;
    const navigate = useNavigate();
    const [active, setActive] = useState(view.tabs[0].key);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const tab = useMemo(() => view.tabs.find((t) => t.key === active) || view.tabs[0], [view, active]);

    useEffect(() => {
        let alive = true;
        setLoading(true); setError(null);
        api.get(tab.url)
            .then(({ data }) => { if (alive) { const d = toCamel(data); setItems(Array.isArray(d?.items) ? d.items : []); } })
            .catch((e) => { if (alive) setError(e?.response?.data?.error || e?.message || "Failed to load"); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [tab]);

    const csvColumns = view.columns.map(([label, get], i) => ({ id: `c${i}`, label, get: (r) => String(get(r) ?? "") }));

    return (
        <div className="page-content">
            <Container fluid>
                <DashboardPageHeader
                    title={view.title}
                    subtitle={`${view.subtitle} (${items.length})`}
                    breadcrumb={[{ label: "Travel Expense", to: "/workflows?wf=travel" }, { label: view.title }]}
                />
                {error && <Alert color="danger" className="mt-2">{error}</Alert>}
                {view.tabs.length > 1 && <ResponsiveTabs tabs={view.tabs} activeKey={active} onChange={setActive} />}
                <Card className="mt-3">
                    <CardBody>
                        {loading ? (
                            <table className="table mb-0"><tbody><TableSkeleton rows={5} columns={view.columns.length + 1} /></tbody></table>
                        ) : items.length === 0 ? (
                            <EmptyState title="Nothing waiting" description="There are no trips in this queue." />
                        ) : (
                            <div className="table-responsive">
                                <div className="d-flex justify-content-end mb-2">
                                    <button type="button" className="btn btn-soft-primary btn-sm"
                                        onClick={() => exportRowsToCsv(`travel-${tab.key}.csv`, csvColumns, items)}>
                                        <i className="ri-download-2-line align-bottom me-1" />Export CSV ({items.length})
                                    </button>
                                </div>
                                <table className="table table-striped table-bordered align-middle mb-0">
                                    <thead className="table-light">
                                        <tr>{view.columns.map(([label]) => <th key={label}>{label}</th>)}<th></th></tr>
                                    </thead>
                                    <tbody>
                                        {items.map((r, i) => (
                                            <tr key={`${r.tripId}-${r.empid ?? i}`}>
                                                {view.columns.map(([label, get]) => <td key={label}>{get(r)}</td>)}
                                                <td>
                                                    <button type="button" className="btn btn-link btn-sm p-0" onClick={() => navigate(view.link(r))}>
                                                        Open
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </CardBody>
                </Card>
            </Container>
        </div>
    );
};

export const TravelHrQueue = () => <TravelBackOfficeQueue kind="hr" />;
export const TravelAccountingQueue = () => <TravelBackOfficeQueue kind="accounting" />;

export default TravelBackOfficeQueue;
